/**
 * PersistentRaven - Keeps Raven iframe mounted across navigation
 *
 * Follows PersistentChatwoot pattern:
 * - Lazy mount: only renders after user first visits /raven
 * - visibility:hidden when navigating away (keeps websocket connections alive)
 * - Shown instantly when returning to /raven
 *
 * URL sync: two-way browser URL ↔ iframe path synchronization
 * - Iframe ROUTE_CHANGE → updates browser URL (push for significant, replace for minor)
 * - Browser back/forward → sends NAVIGATE to iframe
 * - Loop prevention via parentInitiated flag + lastSyncedPath comparison
 */

import { useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import { RavenEmbed } from "@/components/RavenEmbed";
import type { RavenEmbedRef } from "@/components/RavenEmbed/types";
import { useAuthStore } from "@/lib/store/authStore";
import { isSignificantNavigation } from "./urlSyncUtils";

export function PersistentRaven() {
  const location = useLocation();
  const navigate = useNavigate();
  const isOnRavenPage = location.pathname.startsWith("/raven");

  // Lazy mount: only render after first visit
  const [hasVisitedRaven, setHasVisitedRaven] = useState(false);

  // URL sync refs — loop prevention + deep link initialPath
  const lastSyncedPath = useRef('');
  const initialPathRef = useRef<string | undefined>(undefined);
  const parentInitiated = useRef(false);
  const ravenRef = useRef<RavenEmbedRef>(null);

  const isProvisioned = useAuthStore((state) => state.isProvisioned);

  useEffect(() => {
    if (isOnRavenPage && !hasVisitedRaven) {
      // Extract sub-path: "/raven/channel/general" → "/channel/general", "/raven" → ""
      const subPath = location.pathname.slice(6); // Remove "/raven"
      initialPathRef.current = subPath || undefined;
      lastSyncedPath.current = subPath;
      setHasVisitedRaven(true);
    }
  }, [isOnRavenPage, hasVisitedRaven, location.pathname]);

  // Restore last-viewed URL when returning to Raven from another app (sidebar click)
  useEffect(() => {
    if (parentInitiated.current) return; // Back-button effect handles this
    if (isOnRavenPage && hasVisitedRaven && lastSyncedPath.current) {
      const expectedPath = `/raven${lastSyncedPath.current}`;
      if (location.pathname !== expectedPath) {
        navigate(expectedPath, { replace: true });
      }
    }
  }, [isOnRavenPage, hasVisitedRaven, navigate, location.pathname]);

  // Back/forward button: when location changes while on Raven page,
  // send NAVIGATE to iframe if path differs from lastSyncedPath
  useEffect(() => {
    if (!isOnRavenPage || !hasVisitedRaven) return;
    const subPath = location.pathname.slice(6); // Remove "/raven"
    if (subPath && subPath !== lastSyncedPath.current) {
      if (!ravenRef.current?.isReady) return;
      parentInitiated.current = true;
      lastSyncedPath.current = subPath;
      ravenRef.current?.navigate(subPath);
      // Safety: reset parentInitiated if no ROUTE_CHANGE echo within 500ms
      const safetyTimer = setTimeout(() => {
        parentInitiated.current = false;
      }, 500);
      return () => clearTimeout(safetyTimer);
    }
  }, [location.pathname, isOnRavenPage, hasVisitedRaven]);

  const handleReady = useCallback(() => {
    console.log("[PersistentRaven] Ready");
  }, []);

  const handleRouteChange = useCallback((path: string, _title: string) => {
    // Raven sends ABSOLUTE paths via window.location.pathname (raven_embed.js)
    // e.g., "/raven/channel/general" — must strip "/raven" prefix
    const subPath = path.startsWith('/raven') ? path.slice(6) : path;
    if (parentInitiated.current) {
      parentInitiated.current = false;
      return; // Skip — we initiated this navigation via back/forward
    }
    if (lastSyncedPath.current === subPath) return; // Skip duplicate
    const prevPath = lastSyncedPath.current;
    lastSyncedPath.current = subPath;
    if (isOnRavenPage) {
      const shouldPush = isSignificantNavigation(prevPath, subPath);
      navigate(`/raven${subPath}`, { replace: !shouldPush });
    }
  }, [navigate, isOnRavenPage]);

  const handleError = useCallback((message: string) => {
    console.error("[PersistentRaven] Error:", message);
  }, []);

  const handleNavigateParent = useCallback(
    (action: string) => {
      switch (action) {
        case "back":
          navigate(-1);
          break;
        case "home":
          navigate("/");
          break;
        default:
          if (action.startsWith("/")) {
            navigate(action);
          } else {
            console.warn("[PersistentRaven] Unknown navigate action:", action);
          }
      }
    },
    [navigate]
  );

  // Don't render if never visited or not provisioned
  if (!hasVisitedRaven || !isProvisioned) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 top-0 z-10 md:left-[72px] md:right-[48px]"
      style={{
        visibility: isOnRavenPage ? "visible" : "hidden",
        pointerEvents: isOnRavenPage ? "auto" : "none",
      }}
    >
      <RavenEmbed
        ref={ravenRef}
        initialPath={initialPathRef.current}
        className="h-full w-full"
        onReady={handleReady}
        onRouteChange={handleRouteChange}
        onError={handleError}
        onNavigateParent={handleNavigateParent}
      />
    </div>
  );
}
